import { useState, useCallback, useRef } from "react";
import { API } from "constants/RestApi";
import {
  ITenantPreferences,
  ITenantPreferencesUpdate,
} from "core/types/entities/ITenantPreferences";
import { useBaseAction } from "./index";

/**
 * Custom hook for managing tenant preferences in settings
 *
 * Features:
 * - Fetch preferences from API
 * - Update preferences with optimistic updates
 * - Error handling and loading states
 * - Automatic data refresh after updates
 */
export const usePreferences = () => {
  const { request } = useBaseAction();
  const [preferences, setPreferences] = useState<ITenantPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFetching = useRef(false);

  /**
   * Fetches tenant preferences from the API
   * @returns Promise<ITenantPreferences | null>
   */
  const fetchPreferences = useCallback(async (): Promise<ITenantPreferences | null> => {
    // Prevent multiple simultaneous requests
    if (isFetching.current) {
      return preferences;
    }

    try {
      isFetching.current = true;
      setIsLoading(true);
      setError(null);

      const response = await request({
        method: "GET",
        url: API.SETTINGS_TENANT_PREFERENCES,
      });

      setPreferences(response);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch preferences";
      setError(errorMessage);
      console.error("Error fetching preferences:", err);
      return null;
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [preferences]);

  /**
   * Updates tenant preferences via API
   * @param updateData - Preferences data to update
   * @returns Promise<boolean> - Success status
   */
  const updatePreferences = useCallback(
    async (updateData: ITenantPreferencesUpdate): Promise<boolean> => {
      try {
        setIsLoading(true);
        setError(null);

        // Ensure we have current preferences with rowVersion
        if (!preferences || !preferences.rowVersion) {
          throw new Error("Preferences not loaded or missing rowVersion");
        }

        const requestData = {
          ...updateData,
          rowVersion: preferences.rowVersion,
        };

        const response = await request({
          method: "PUT",
          url: API.SETTINGS_TENANT_PREFERENCES,
          body: requestData,
          successMessage: "Preferences updated successfully",
          useLoader: true,
        });

        // Update local state with the response
        if (response) {
          setPreferences(response);
        }

        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to update preferences";
        setError(errorMessage);
        console.error("Error updating preferences:", err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [preferences],
  );

  /**
   * Clears the current preferences and error state
   */
  const clearPreferences = useCallback(() => {
    setPreferences(null);
    setError(null);
  }, []);

  return {
    preferences,
    isLoading,
    error,
    fetchPreferences,
    updatePreferences,
    clearPreferences,
  };
};
